import React, { useEffect, useRef, useState, useCallback } from 'react'
import PageHeader from '../utils/PageHeader'
import { API_URL } from '../utils/Constants/URLS'
import PageLoader from '../utils/PageLoader'

/* ─── column definitions ─────────────────────────────────────────────────────
   Order must match both the table <th> list and the CSV export.
────────────────────────────────────────────────────────────────────────────── */
const COLUMNS = [
  'Credit Note No',
  'Credit Note Date',
  'Status',
  'Is E-Invoice',
  'Original Invoice No',
  'Original Invoice Date',
  'Executive Region',
  'Executive Name',
  'Store Name',
  'Buyer PO No',
  'Buyer PO Date',
  'Buyer Name',
  'Buyer GSTIN',
  'Bill to Address',
  'Consignee Name',
  'Consignee GSTIN',
  'Ship to Address',
  'SO ID',
  'Place of Supply',
  'Supply Type',
  'No of Line Items',
  'Taxable Value',
  'CGST Value',
  'SGST Value',
  'IGST Value',
  'Round Off',
  'Total Credit Note Value',
  'Despatch Doc No',
  'Despatch through',
  'Vehicle Details',
  'Other Reference',
]

const CURRENCY_COLS = new Set([
  'Taxable Value',
  'CGST Value',
  'SGST Value',
  'IGST Value',
  'Round Off',
  'Total Credit Note Value',
])

export default function AggCreditNoteReport() {
  const token = localStorage.getItem('token')
  const _now = new Date()
  const _year = _now.getFullYear()
  const _month = String(_now.getMonth() + 1).padStart(2, '0')
  const defaultStart = `${_year}-${_month}-01`
  const defaultEnd = _now.toISOString().slice(0, 10)

  const [data, setData] = useState([])
  const [summary, setSummary] = useState(null)
  const [startDate, setStartDate] = useState(defaultStart)
  const [endDate, setEndDate] = useState(defaultEnd)
  const [loading, setLoading] = useState(false)
  const [pageLoading, setPageLoading] = useState(false)
  const [error, setError] = useState(null)
  const [isDownloading, setIsDownloading] = useState(false)

  const [page, setPage] = useState(1)
  const limit = 10
  const [hasMore, setHasMore] = useState(true)

  const observerRef = useRef(null)
  const loadingRef = useRef(false)
  const pageLoadingRef = useRef(false)

  const buildUrl = (p = page) => {
    const s = encodeURIComponent(startDate)
    const e = encodeURIComponent(endDate)
    return `${API_URL}credit-notes/report/credit-note-wise?startDate=${s}&endDate=${e}&page=${p}&limit=${limit}`
  }

  const fetchPage = useCallback(
    async (p = 1, replace = false) => {
      try {
        if (p === 1 && replace) {
          setLoading(true)
          loadingRef.current = true
        } else {
          setPageLoading(true)
          pageLoadingRef.current = true
        }
        setError(null)

        const res = await fetch(buildUrl(p), {
          method: 'GET',
          headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${token}`,
          },
        })

        if (!res.ok) throw new Error(`Server responded with ${res.status}`)
        const result = await res.json()

        const docs = result?.data?.reportData || []
        const paginationInfo = result?.data?.pagination || {}
        const summaryInfo = result?.data?.summary || null

        setData(prev => (p === 1 && replace ? docs : [...prev, ...docs]))
        if (p === 1 && replace) setSummary(summaryInfo)

        const hasNextPage =
          paginationInfo.hasNextPage !== undefined
            ? paginationInfo.hasNextPage
            : docs.length >= limit
        setHasMore(hasNextPage)
      } catch (err) {
        setError(err.message || 'Failed to fetch')
      } finally {
        setLoading(false)
        loadingRef.current = false
        setPageLoading(false)
        pageLoadingRef.current = false
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [token, startDate, endDate]
  )

  useEffect(() => {
    setPage(1)
    fetchPage(1, true)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token, startDate, endDate])

  useEffect(() => {
    if (!hasMore) return
    const sentinel = observerRef.current
    if (!sentinel) return

    const io = new IntersectionObserver(
      entries => {
        entries.forEach(entry => {
          if (entry.isIntersecting && !pageLoadingRef.current && !loadingRef.current) {
            setPage(prev => prev + 1)
          }
        })
      },
      { root: null, rootMargin: '200px', threshold: 0.1 }
    )

    io.observe(sentinel)
    return () => io.disconnect()
  }, [hasMore])

  useEffect(() => {
    if (page === 1) return
    fetchPage(page)
  }, [page, fetchPage])

  const handleFetchClick = () => {
    if (startDate > endDate) {
      setError('Start date cannot be after end date')
      return
    }
    setError(null)
    setPage(1)
    fetchPage(1, true)
  }

  const handleReset = () => {
    setStartDate(defaultStart)
    setEndDate(defaultEnd)
    setError(null)
    setPage(1)
    fetchPage(1, true)
  }

  const quote = value => {
    if (value === null || value === undefined) return '""'
    const str = String(value)
    return `"${str.replace(/"/g, '""')}"`
  }

  const fmtDate = val =>
    val
      ? new Date(val).toLocaleDateString('en-GB', {
          day: '2-digit',
          month: 'short',
          year: 'numeric',
        })
      : ''

  const fmtCurrency = val => {
    const n = Number(val)
    return isNaN(n) ? '' : n.toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })
  }

  const fmtBool = val => {
    if (val === true || val === 'true' || val === 'Yes' || val === 'yes') return 'Yes'
    if (val === false || val === 'false' || val === 'No' || val === 'no') return 'No'
    return val ?? ''
  }

  const rowToObj = dt => ({
    'Credit Note No': dt['Credit Note No'] ?? dt.creditNoteNumber ?? '',
    'Credit Note Date': fmtDate(dt['Credit Note Date'] ?? dt.creditNoteDate),
    'Status': dt['Status'] ?? dt.status ?? '',
    'Is E-Invoice': fmtBool(dt['Is E-Invoice'] ?? dt.isEInvoice),
    'Original Invoice No': dt['Original Invoice No'] ?? dt.originalInvoiceNumber ?? '',
    'Original Invoice Date': fmtDate(dt['Original Invoice Date'] ?? dt.originalInvoiceDate),
    'Executive Region': Array.isArray(dt['Executive Region'])
      ? dt['Executive Region'].join(', ')
      : dt['Executive Region'] ?? '',
    'Executive Name': dt['Executive Name'] ?? '',
    'Store Name': dt['Store Name'] ?? '',
    'Buyer PO No': dt['Buyer PO No'] ?? '',
    'Buyer PO Date': fmtDate(dt['Buyer PO Date']),
    'Buyer Name': dt['Buyer Name'] ?? '',
    'Buyer GSTIN': dt['Buyer GSTIN'] ?? '',
    'Bill to Address': dt['Bill to Address'] ?? '',
    'Consignee Name': dt['Consignee Name'] ?? '',
    'Consignee GSTIN': dt['Consignee GSTIN'] ?? '',
    'Ship to Address': dt['Ship to Address'] ?? '',
    'SO ID': dt['SO ID'] ?? '',
    'Place of Supply': dt['Place of Supply'] ?? '',
    'Supply Type': dt['Supply Type'] ?? '',
    'No of Line Items': dt['No of Line Items'] ?? '',
    'Taxable Value': dt['Taxable Value'] ?? '',
    'CGST Value': dt['CGST Value'] ?? dt['CGST'] ?? '',
    'SGST Value': dt['SGST Value'] ?? dt['SGST'] ?? '',
    'IGST Value': dt['IGST Value'] ?? dt['IGST'] ?? '',
    'Round Off': dt['Round Off'] ?? '',
    'Total Credit Note Value': dt['Total Credit Note Value'] ?? '',
    'Despatch Doc No': dt['Despatch Doc No'] ?? '',
    'Despatch through': dt['Despatch through'] ?? '',
    'Vehicle Details': dt['Vehicle Details'] ?? '',
    'Other Reference': dt['Other Reference'] ?? '',
  })

  const downloadCsv = async () => {
    if (isDownloading) return

    try {
      setIsDownloading(true)
      setError(null)

      const s = encodeURIComponent(startDate)
      const e = encodeURIComponent(endDate)
      const fullUrl = `${API_URL}credit-notes/report/credit-note-wise?startDate=${s}&endDate=${e}&pagination=false`

      const res = await fetch(fullUrl, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
      })

      if (!res.ok) throw new Error(`Server responded with ${res.status}`)

      const result = await res.json()
      const allData = result?.data?.reportData || []

      if (allData.length === 0) {
        setError('No data found for the selected date range.')
        return
      }

      const headerRow = COLUMNS.map(h => quote(h)).join(',')
      const csvLines = allData.map(dt => {
        const obj = rowToObj(dt)
        return COLUMNS.map(h => quote(obj[h])).join(',')
      })

      const csvContent = [headerRow, ...csvLines].join('\r\n')
      const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' })
      const url = URL.createObjectURL(blob)

      const link = document.createElement('a')
      link.href = url
      link.setAttribute('download', `credit_note_wise_report_${startDate}_to_${endDate}.csv`)
      document.body.appendChild(link)
      link.click()
      document.body.removeChild(link)
      URL.revokeObjectURL(url)
    } catch (err) {
      console.error('Download Error:', err)
      setError(err.message || 'Failed to download the complete report.')
    } finally {
      setIsDownloading(false)
    }
  }

  return (
    <>
      <PageHeader
        pageName='CREDIT NOTE WISE REPORT'
        breadcrump1='Reports'
        breadcrump2='Credit Note Wise Report'
        link='/credit-note-wise-report'
      />

      <div className='p-6 min-h-screen'>

        {summary && (
          <div className='max-w-[1400px] mx-2 mb-4 bg-white border border-gray-200 p-4 rounded-md'>
            <div className='flex flex-wrap gap-6 text-sm text-gray-700'>
              <div>
                <span className='font-semibold'>Total Credit Notes:</span>{' '}
                {summary.totalCreditNotes}
              </div>
              <div>
                <span className='font-semibold'>Total Taxable Value:</span>{' '}
                ₹{Math.round(summary.totalTaxableValue)?.toLocaleString('en-IN')}
              </div>
              <div>
                <span className='font-semibold'>Total Credit Note Value:</span>{' '}
                ₹{Math.round(summary.totalCreditNoteValue)?.toLocaleString('en-IN')}
              </div>
            </div>
          </div>
        )}

        <div className='max-w-[1400px] mx-2 mb-4 bg-white border border-gray-200 p-4 rounded-md'>
          <div className='flex flex-col md:flex-row md:items-end md:justify-between gap-3'>
            <div className='flex flex-col md:flex-row md:items-end gap-3'>
              <div className='flex items-center gap-2'>
                <label className='text-sm font-medium'>Start Date</label>
                <input
                  type='date'
                  value={startDate}
                  onChange={e => setStartDate(e.target.value)}
                  className='px-2 py-1 border rounded'
                  max={endDate}
                />
              </div>

              <div className='flex items-center gap-2'>
                <label className='text-sm font-medium'>End Date</label>
                <input
                  type='date'
                  value={endDate}
                  onChange={e => setEndDate(e.target.value)}
                  className='px-2 py-1 border rounded'
                  min={startDate}
                />
              </div>

              <div className='flex items-center gap-2 mt-2 md:mt-0'>
                <button
                  onClick={handleFetchClick}
                  className='px-4 py-2 bg-indigo-600 text-white rounded hover:bg-indigo-700'
                >
                  Fetch
                </button>
                <button
                  onClick={handleReset}
                  className='px-4 py-2 border rounded hover:bg-gray-50'
                >
                  Reset
                </button>
              </div>
            </div>

            <div className='flex items-center justify-end'>
              <button
                onClick={downloadCsv}
                disabled={isDownloading}
                className={`px-4 py-2 text-white rounded transition-colors ${
                  isDownloading
                    ? 'bg-indigo-400 cursor-not-allowed'
                    : 'bg-indigo-600 hover:bg-indigo-700'
                }`}
              >
                {isDownloading ? (
                  <div className='flex items-center gap-2'>
                    <div className='w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin' />
                    Processing...
                  </div>
                ) : (
                  'Download CSV'
                )}
              </button>
            </div>
          </div>

          {error && <div className='mt-3 text-sm text-red-600'>{error}</div>}
        </div>

        <div className='overflow-x-auto bg-white border border-gray-200 max-w-[1400px] mx-2 relative'>
          {loading && (
            <div className='absolute inset-0 z-50 flex items-center justify-center bg-white/60'>
              <PageLoader />
            </div>
          )}

          <table className='min-w-full border-collapse table-auto'>
            <thead className='bg-gradient-to-r from-indigo-500 to-indigo-600 text-white text-sm font-semibold uppercase tracking-wide'>
              <tr>
                {COLUMNS.map(col => (
                  <th
                    key={col}
                    className='px-3 py-2 border-b border-gray-300 text-left whitespace-nowrap'
                  >
                    {col}
                  </th>
                ))}
              </tr>
            </thead>

            <tbody className='text-sm text-gray-800'>
              {data.map((dt, index) => {
                const obj = rowToObj(dt)
                return (
                  <tr
                    key={`${dt?.creditNoteNumber ?? dt?.['Credit Note No'] ?? 'row'}-${index}`}
                    className='hover:bg-indigo-50 transition-colors'
                  >
                    {COLUMNS.map(col => (
                      <td
                        key={col}
                        className={`px-4 py-3 border-b border-gray-200 whitespace-nowrap ${
                          CURRENCY_COLS.has(col) ? 'text-right tabular-nums' : ''
                        }`}
                      >
                        {CURRENCY_COLS.has(col)
                          ? fmtCurrency(obj[col])
                          : obj[col]}
                      </td>
                    ))}
                  </tr>
                )
              })}
            </tbody>
          </table>

          <div ref={observerRef} className='h-8 flex items-center justify-center'>
            {pageLoading && <PageLoader />}
            {!hasMore && !loading && (
              <div className='p-4 text-center text-sm text-gray-500'>No more records.</div>
            )}
          </div>

          {!loading && data.length === 0 && !error && (
            <div className='p-6 text-center text-sm text-gray-600'>
              No records found for selected date range.
            </div>
          )}
        </div>
      </div>
    </>
  )
}
